import React, { useRef, useState, useEffect, useCallback } from "react";

interface Props {
  children: React.ReactNode;
  className?: string;
}

const SCROLLABLE_TABLE_SELECTORS =
  ".ant-table-body, .ant-table-content, .ant-table-header";

const getTableScrollElement = (container: HTMLElement): HTMLElement | null => {
  const candidates = container.querySelectorAll(SCROLLABLE_TABLE_SELECTORS);

  for (const node of candidates) {
    const el = node as HTMLElement;
    const { overflowX } = window.getComputedStyle(el);

    if (
      (overflowX === "auto" || overflowX === "scroll") &&
      el.scrollWidth > el.clientWidth
    ) {
      return el;
    }
  }

  return (
    (container.querySelector(".ant-table-body") as HTMLElement | null) ||
    (container.querySelector(".ant-table-content") as HTMLElement | null)
  );
};

const hideNativeScrollbars = (container: HTMLElement) => {
  container.querySelectorAll(SCROLLABLE_TABLE_SELECTORS).forEach((node) => {
    const el = node as HTMLElement;
    el.style.setProperty("scrollbar-width", "none");
    el.style.setProperty("-ms-overflow-style", "none");
  });
};

const getScrollParent = (el: HTMLElement | null): HTMLElement | null => {
  return el?.closest(".content-container") as HTMLElement | null;
};

const HorizontalScrollContainer = ({ children, className }: Props) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollTargetRef = useRef<HTMLElement | null>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [thumbWidth, setThumbWidth] = useState(0);
  const [thumbLeft, setThumbLeft] = useState(0);
  const [showScrollbar, setShowScrollbar] = useState(false);
  const [floatingStyle, setFloatingStyle] = useState<React.CSSProperties | null>(
    null
  );
  const dragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartScrollLeft = useRef(0);

  const resolveScrollTarget = useCallback(() => {
    const container = containerRef.current;
    if (!container) return null;

    const target = getTableScrollElement(container);
    scrollTargetRef.current = target;
    return target;
  }, []);

  const updateFloatingPosition = useCallback(() => {
    const root = rootRef.current;
    const content = containerRef.current;
    if (!root || !content || !showScrollbar) {
      setFloatingStyle(null);
      return;
    }

    const scrollParent = getScrollParent(root);
    if (!scrollParent) {
      setFloatingStyle(null);
      return;
    }

    const parentRect = scrollParent.getBoundingClientRect();
    const contentRect = content.getBoundingClientRect();
    const rootRect = root.getBoundingClientRect();

    const tableVisible =
      contentRect.bottom > parentRect.top + 40 &&
      contentRect.top < parentRect.bottom - 20;

    // Float the bar at the bottom of the visible area when the natural
    // scrollbar position is below the fold.
    const shouldFloat =
      tableVisible && rootRect.bottom > parentRect.bottom - 8;

    if (!shouldFloat) {
      setFloatingStyle(null);
      return;
    }

    setFloatingStyle({
      position: "fixed",
      left: contentRect.left,
      width: contentRect.width,
      bottom: Math.max(window.innerHeight - parentRect.bottom + 8, 8),
      zIndex: 50,
    });
  }, [showScrollbar]);

  const updateThumb = useCallback(() => {
    const el = scrollTargetRef.current || resolveScrollTarget();
    if (!el) return;

    const { scrollWidth, clientWidth, scrollLeft } = el;
    const needsScroll = scrollWidth > clientWidth + 1;
    setShowScrollbar(needsScroll);

    if (!needsScroll) {
      setFloatingStyle(null);
      return;
    }

    const trackWidth = trackRef.current?.clientWidth || clientWidth;
    const width = Math.max((clientWidth / scrollWidth) * trackWidth, 40);
    const maxThumbLeft = trackWidth - width;
    const maxScrollLeft = scrollWidth - clientWidth;
    const left =
      maxScrollLeft > 0 ? (scrollLeft / maxScrollLeft) * maxThumbLeft : 0;

    setThumbWidth(width);
    setThumbLeft(left);
    updateFloatingPosition();
  }, [resolveScrollTarget, updateFloatingPosition]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const onScroll = () => updateThumb();
    const scrollParent = getScrollParent(container);

    const attach = () => {
      hideNativeScrollbars(container);
      const target = resolveScrollTarget();
      if (!target) return;

      target.removeEventListener("scroll", onScroll);
      target.addEventListener("scroll", onScroll, { passive: true });
      updateThumb();
      updateFloatingPosition();
    };

    attach();

    const ro = new ResizeObserver(attach);
    ro.observe(container);

    const mo = new MutationObserver(attach);
    mo.observe(container, { childList: true, subtree: true });

    window.addEventListener("resize", attach);
    scrollParent?.addEventListener("scroll", updateFloatingPosition, {
      passive: true,
    });

    return () => {
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener("resize", attach);
      scrollParent?.removeEventListener("scroll", updateFloatingPosition);
      scrollTargetRef.current?.removeEventListener("scroll", onScroll);
    };
  }, [updateThumb, updateFloatingPosition, resolveScrollTarget, children]);

  useEffect(() => {
    updateFloatingPosition();
  }, [showScrollbar, updateFloatingPosition]);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!dragging.current) return;

      const el = scrollTargetRef.current;
      if (!el) return;

      const { scrollWidth, clientWidth } = el;
      const maxScrollLeft = scrollWidth - clientWidth;
      const trackWidth = trackRef.current?.clientWidth || clientWidth;
      const maxThumbLeft = trackWidth - thumbWidth;
      const deltaX = e.clientX - dragStartX.current;
      const scrollDelta =
        maxThumbLeft > 0 ? (deltaX / maxThumbLeft) * maxScrollLeft : 0;

      el.scrollLeft = dragStartScrollLeft.current + scrollDelta;
    };

    const onMouseUp = () => {
      dragging.current = false;
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);

    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, [thumbWidth]);

  const onThumbMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragging.current = true;
    dragStartX.current = e.clientX;
    dragStartScrollLeft.current = scrollTargetRef.current?.scrollLeft || 0;
  };

  const onTrackClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = scrollTargetRef.current;
    if (!el || !trackRef.current) return;
    if ((e.target as HTMLElement).classList.contains("horizontal-scroll-thumb")) {
      return;
    }

    const rect = trackRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const { scrollWidth, clientWidth } = el;
    const maxScrollLeft = scrollWidth - clientWidth;
    const trackWidth = trackRef.current.clientWidth;

    el.scrollLeft = (clickX / trackWidth) * maxScrollLeft;
  };

  return (
    <div
      ref={rootRef}
      className={`horizontal-scroll-container ${className || ""}`}
    >
      <div ref={containerRef} className="horizontal-scroll-content">
        {children}
      </div>
      {showScrollbar && (
        <>
          {/* Keeps layout space when the real track is floating */}
          {floatingStyle && <div className="horizontal-scroll-track-spacer" />}
          <div
            ref={trackRef}
            className={`horizontal-scroll-track${
              floatingStyle ? " horizontal-scroll-track-floating" : ""
            }`}
            style={floatingStyle || undefined}
            onClick={onTrackClick}
          >
            <div
              className="horizontal-scroll-thumb"
              style={{
                width: thumbWidth,
                transform: `translateX(${thumbLeft}px)`,
              }}
              onMouseDown={onThumbMouseDown}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default HorizontalScrollContainer;
